import { Box, Fade, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

interface WearableMetricDetailPanelProps {
  color: string;
  label: string;
  meta?: string;
  visible: boolean;
  headerExtra?: ReactNode;
  children: ReactNode;
}

export function WearableMetricDetailPanel({
  color,
  label,
  meta,
  visible,
  headerExtra,
  children,
}: WearableMetricDetailPanelProps) {
  if (!visible) return null;

  return (
    <Fade in={visible} timeout={220}>
      <Box sx={{ mt: 0.5, width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
          <Box
            sx={{
              width: 3,
              height: 20,
              bgcolor: color,
              borderRadius: 1,
              opacity: 0.45,
            }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: `${color}45`,
            borderBottom: `3px solid ${color}`,
            borderRight: `4px solid ${color}`,
            borderRadius: 2,
            bgcolor: `${color}07`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1.5,
              borderBottom: "1px solid",
              borderColor: `${color}22`,
              bgcolor: `${color}0c`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ width: "100%" }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1.05rem" }}>
                  {label}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                {headerExtra}
                {meta ? (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      color,
                      bgcolor: `${color}18`,
                      border: "1px solid",
                      borderColor: `${color}35`,
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 1,
                      whiteSpace: "nowrap",
                      fontSize: "0.9rem",
                    }}
                  >
                    {meta}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            {children}
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
}
